"use client";

import { useEffect, useRef, useState } from "react";

export type DefaultImageType =
  | "general"
  | "product"
  | "category"
  | "productShowcase"
  | "categoryShowcase"
  | "banner"
  | "cmsPage";

export type ResolvedDefaultImage = { url: string; altText?: string | null };
export type ResolvedDefaultImages = Partial<Record<DefaultImageType, ResolvedDefaultImage>>;

export const STATIC_FALLBACK = "/images/defaults/placeholder-general.svg";

/** Same real -> type -> general -> static priority as <SafeImage>, for the rare caller
 * that can't render a plain <img> (e.g. next/image) and so can't use onError recovery —
 * only the first (empty-src) case is covered, not broken-URL recovery. */
export function resolveFallbackImageUrl(src: string | null | undefined, fallbackType: DefaultImageType, defaults?: ResolvedDefaultImages): string {
  const trimmedSrc = src?.trim();
  if (trimmedSrc) return trimmedSrc;
  return defaults?.[fallbackType]?.url || defaults?.general?.url || STATIC_FALLBACK;
}

type Props = {
  /** The real content image URL, if any — null/undefined/empty/whitespace all count as "no image". */
  src?: string | null;
  /** Which admin-configured default to use when src is missing or fails to load. */
  fallbackType: DefaultImageType;
  /** Resolved once per page via apiClient.getDefaultImages() and passed down — never fetched per-image. */
  defaults?: ResolvedDefaultImages;
  /** Always the real content's name — never derived from the fallback image's file name. */
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
};

/**
 * Fallback chain: real image -> type-specific default -> General default -> static bundled
 * asset. A broken/404 URL at any step advances to the next one via onError; the chain is a
 * fixed-length array so this can advance at most 3 times and then stops (no infinite loop).
 */
export function SafeImage({ src, fallbackType, defaults, alt, className, style, onClick }: Props) {
  const chain = buildChain(src, fallbackType, defaults);
  const [step, setStep] = useState(0);

  useEffect(() => {
    setStep(0);
  }, [src, fallbackType, defaults]);

  const safeStep = Math.min(step, chain.length - 1);
  const currentSrc = chain[safeStep];
  const imgRef = useRef<HTMLImageElement>(null);

  // Server-rendered <img> can fail before React attaches onError (the event is then never seen again): once mounted, an
  // element that is already `complete` with no pixels is a failed load, so advance the chain the same way onError would.
  useEffect(() => {
    const el = imgRef.current;
    // (SVGs without an intrinsic size legitimately report 0 in some browsers, so they are left to onError.)
    if (el && el.complete && el.naturalWidth === 0 && !/\.svg(\?|$)/i.test(el.currentSrc)) setStep((s) => (s < chain.length - 1 ? s + 1 : s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSrc]);

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={imgRef}
      src={currentSrc}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
      onError={() => setStep((s) => (s < chain.length - 1 ? s + 1 : s))}
    />
  );
}

function buildChain(src: string | null | undefined, fallbackType: DefaultImageType, defaults?: ResolvedDefaultImages): string[] {
  const chain: string[] = [];
  const trimmedSrc = src?.trim();
  if (trimmedSrc) chain.push(trimmedSrc);

  const typeUrl = defaults?.[fallbackType]?.url;
  if (typeUrl) chain.push(typeUrl);

  const generalUrl = defaults?.general?.url;
  if (generalUrl && generalUrl !== typeUrl) chain.push(generalUrl);

  chain.push(STATIC_FALLBACK);
  return chain;
}
